<script setup lang="ts">
import { useSubscribeStockAlertMutation } from '~~/app/composables/queries/useStoreApi'

const props = defineProps<{
  productId: string
  isFavorite?: boolean
}>()

const emit = defineEmits<{
  toggleWishlist: []
}>()

const email = ref('')
const submitted = ref(false)
const errorMsg = ref('')

const mutation = useSubscribeStockAlertMutation()
// Ulubione wymagają konta — w sklepie katalogowym ich nie ma.
const { sellingEnabled } = useCatalogOnly()

function validateEmail(val: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
}

async function submit() {
  errorMsg.value = ''
  if (!validateEmail(email.value)) {
    errorMsg.value = 'Podaj poprawny adres e-mail.'
    return
  }
  mutation.mutate(
    { id_product: Number(props.productId), email: email.value.trim() },
    {
      onSuccess: () => { submitted.value = true },
      onError: () => { errorMsg.value = 'Wystąpił błąd. Spróbuj ponownie.' },
    },
  )
}
</script>

<template>
  <div class="stock-alert">
    <template v-if="!submitted">
      <p class="stock-alert__text">
        Aktualnie nie posiadamy tego produktu. Powiadomimy cię gdy produkt będzie ponownie dostępny w sprzedaży!
      </p>
      <form class="stock-alert__form" @submit.prevent="submit">
        <input v-model="email" type="email" placeholder="Wpisz swój email" class="stock-alert__input" autocomplete="email" />
        <div class="stock-alert__actions">
          <button type="submit" class="stock-alert__submit" :disabled="mutation.isPending.value">
            Powiadom mnie
          </button>
          <button
            v-if="sellingEnabled"
            type="button"
            class="stock-alert__wishlist"
            :class="{ 'is-favorite': props.isFavorite }"
            :aria-label="props.isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'"
            @click="emit('toggleWishlist')"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" :fill="props.isFavorite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        </div>
      </form>
      <p v-if="errorMsg" class="stock-alert__error">{{ errorMsg }}</p>
    </template>

    <p v-else class="stock-alert__success">
      Zapisano! Poinformujemy Cię gdy produkt będzie dostępny.
    </p>
  </div>
</template>

<style scoped>
.stock-alert {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stock-alert__text {
  font-size: 0.875rem;
  color: var(--color-muted, #666);
  margin: 0;
}

.stock-alert__form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stock-alert__input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1.5px solid var(--color-border, #e0e0e0);
  border-radius: 999px;
  font-size: 1.3rem;
  font-family: var(--font-body);
  background: #fff;
}

.stock-alert__actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.stock-alert__submit {
  flex: 1;
  height: 56px;
  padding: 0 24px;
  background: var(--color-primary, #111);
  color: #fff;
  border: 1px solid var(--color-primary, #111);
  border-radius: var(--btn-pill-radius);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.04em;
  font-family: var(--font-body);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.stock-alert__submit:hover:not(:disabled) {
  background: var(--color-white, #fff);
  color: var(--color-primary, #111);
}

.stock-alert__wishlist {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-md, #d9d9d9);
  background: var(--color-white, #fff);
  color: var(--color-black, #111);
  border-radius: var(--btn-pill-radius);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.stock-alert__wishlist:hover {
  background: var(--color-primary, #111);
  color: var(--color-white, #fff);
  border-color: var(--color-primary, #111);
}

.stock-alert__wishlist.is-favorite {
  color: var(--color-sale, #e53e3e);
}

@media (max-width: 767px) {
  .stock-alert__actions {
    gap: 8px;
  }
}

.stock-alert__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stock-alert__error {
  font-size: 1.2rem;
  color: var(--color-sale, #e53e3e);
  margin: 0;
}

.stock-alert__success {
  font-size: 1.3rem;
  color: var(--color-success, #2d8a4e);
  margin: 0;
}
</style>
